/* eslint-disable camelcase */
import * as Sentry from '@sentry/react';
import SegmentUtils from '@spothero/utils/segment';
import OptimizelyUtils from '@spothero/utils/optimizely';
import EnvironmentUtils from '@spothero/utils/environment';
import {spotheroGet} from 'api';
import WootricUtils from 'utils/wootric';
import IntercomUtils from 'utils/intercom';
import Config from 'hooks/config';

type UserData = {
    email: string;
    user_id: number;
    is_staff: boolean;
    date_joined: string;
    facility_count?: number; // Optional, not used in this code
};

const TrackingUtils = {
    /**
     * Initializes the tracking functionality for the control panel.
     */
    async init(): Promise<void> {
        const {
            isDeployed,
            isProduction,
            segmentApiKey,
            isSegmentEnabled,
            isWootricEnabled,
            forceWootricSurvey,
            wootricAccountToken,
            debugSegmentInConsole,
            debugWootricInConsole,
        } = Config;

        if (isSegmentEnabled) {
            await OptimizelyUtils.initialize(null, {
                loadMechanism: (path: string) => spotheroGet(path),
                isDev: !isProduction,
            });

            SegmentUtils.init({
                apiKey: segmentApiKey,
                debug: debugSegmentInConsole,
            });
        }

        if (isWootricEnabled) {
            WootricUtils.init({
                debug: debugWootricInConsole,
                forceSurvey: forceWootricSurvey,
                accountToken: isDeployed ? wootricAccountToken : 'debug',
            });
        }
    },

    /**
     * Sets the user attributes for tracking, including Segment, Sentry, Optimizely, and Wootric.
     * @param userData - User data passed to the function.
     */
    setUserAttributes(userData: UserData): void {
        const {
            email,
            user_id: id,
            is_staff: isStaff,
            date_joined: dateJoined,
        } = userData;
        const {
            isDeployed,
            isProduction,
            isSegmentEnabled,
            sentryAPIPublicKey,
            isWootricEnabled,
            isMobileDevice,
        } = Config;

        if (isSegmentEnabled) {
            OptimizelyUtils.setUserAttributes(
                {
                    email: email || null,
                    staff: Boolean(isStaff).toString(),
                    browser: EnvironmentUtils.getBrowserCategory(),
                    client: isMobileDevice ? 'mobile-web' : 'web',
                    environment: Config.envType
                        ? Config.envType
                        : isProduction
                          ? 'production'
                          : 'staging',
                },
                {reset: true}
            );

            SegmentUtils.onReady(() => {
                const segmentTraits = {
                    Platform: isMobileDevice ? 'Mobile Web' : 'Web',
                    Username: email,
                    email,
                    userId: id,
                    'SpotHero Admin': isStaff,
                };

                SegmentUtils.identify({
                    uniqueId: id,
                    traits: segmentTraits,
                });
            });
        }

        if (isWootricEnabled) {
            WootricUtils.setSettings({
                settings: {
                    email,
                    created_at: Math.floor(
                        new Date(dateJoined).getTime() / 1000
                    ),
                },
            });

            WootricUtils.runSurvey();
        }

        if (isProduction) {
            IntercomUtils.init({
                user_id: String(id),
                email,
                date_joined: dateJoined,
            });
        }

        if (isDeployed) {
            // Build Sentry configuration
            const sentryConfig: Sentry.BrowserOptions = {
                dsn: sentryAPIPublicKey,
                release: Config.buildTag,
                environment: isProduction ? 'production' : 'staging',
                sendDefaultPii: true,
                normalizeDepth: 10,
                allowUrls: ['spothero.com', 'kickthe.tires'],
                attachStacktrace: true,
                ignoreErrors: [
                    /login\srequired/i,
                    "TypeError: Cannot read properties of undefined (reading 'baseURL')",
                    'CanceledError: canceled',
                ],
                ...(isProduction && {
                    replaysSessionSampleRate: isStaff ? 0 : 1.0,
                    replaysOnErrorSampleRate: 1.0,
                    integrations: [
                        Sentry.replayIntegration({
                            maskAllText: false,
                            maskAllInputs: false,
                            blockAllMedia: false,
                            networkDetailAllowUrls: [
                                window.location.origin,
                                /https:\/\/services\.production\.spothero\.com\/.*/,
                                /\/api\/v1\/.*/,
                                /https:\/\/(staging\.)?spothero\.com\/.*/,
                                /https:\/\/api\.segment\.io\/v1\/.*/,
                                /https:\/\/api-js\.mixpanel\.com\/*/,
                            ],
                            networkRequestHeaders: [
                                'authorization',
                                'cookie',
                                'referer',
                                'x-csrftoken',
                            ],
                        }),
                    ],
                }),
                initialScope: (scope: Sentry.Scope) => {
                    scope.setUser({
                        id: String(id),
                        email,
                    });

                    return scope;
                },
                beforeSend: (event) => {
                    if (event.exception) {
                        const filteredExceptions =
                            event.exception.values?.filter(
                                (value) =>
                                    value.type !== 'React ErrorBoundary Error'
                            );

                        return {
                            ...event,
                            exception: {values: filteredExceptions},
                        };
                    }

                    return event;
                },
            };

            Sentry.init(sentryConfig);
        }
    },
};

export default TrackingUtils;
